import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  message,
  Spin,
  Tag,
  Divider,
  Select,
  Alert,
  Modal,
  Space,
  Tooltip,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  GlobalOutlined,
  ShopOutlined,
  SaveOutlined,
  LockOutlined,
  KeyOutlined,
  EyeOutlined,
  CopyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import axios from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const countryOptions = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'CN', name: 'China' },
  { code: 'SG', name: 'Singapore' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TH', name: 'Thailand' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IN', name: 'India' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
];

const MyAccount = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const { refetch } = useAuth();

  // API Key states
  const [maskedApiKey, setMaskedApiKey] = useState(null);
  const [apiKeyCreatedAt, setApiKeyCreatedAt] = useState(null);
  const [revealedApiKey, setRevealedApiKey] = useState(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKeyPasswordInput, setApiKeyPasswordInput] = useState('');
  const [apiKeyAction, setApiKeyAction] = useState('reveal');
  const [apiKeyVerifyLoading, setApiKeyVerifyLoading] = useState(false);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/client/profile');
      const { user, client } = response.data;
      const data = { ...user, ...client };
      setProfileData(data);
      form.setFieldsValue({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: client.phone,
        companyName: client.companyName,
        name: client.name,
        address: client.address,
        country: client.country,
        currency: client.currency,
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      message.error(
        error?.response?.data?.message || 'Failed to load profile information'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (values) => {
    try {
      setSaving(true);
      await axios.put('/client/profile', values);
      message.success('Profile updated successfully');
      await fetchProfile();
      refetch();
    } catch (error) {
      console.error('Failed to update profile:', error);
      message.error(
        error?.response?.data?.message || 'Failed to update profile'
      );
    } finally {
      setSaving(false);
    }
  };

  // ---- API Key ----
  const fetchApiKey = async () => {
    try {
      setApiKeyLoading(true);
      const res = await axios.get('/client/api-key');
      if (res.data.success) {
        setMaskedApiKey(res.data.apiKey);
        setApiKeyCreatedAt(res.data.createdAt);
      }
    } catch (error) {
      console.error('Failed to fetch API key:', error);
      message.error(
        error?.response?.data?.message || 'Failed to load API key'
      );
    } finally {
      setApiKeyLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKey();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openApiKeyModal = (action) => {
    setApiKeyAction(action);
    setApiKeyPasswordInput('');
    setRevealedApiKey(null);
    setShowApiKeyModal(true);
  };

  const handleApiKeyPasswordSubmit = async () => {
    if (!apiKeyPasswordInput) {
      message.warning('Please enter your password');
      return;
    }

    setApiKeyVerifyLoading(true);
    try {
      const endpoint =
        apiKeyAction === 'reveal'
          ? '/client/api-key/reveal'
          : '/client/api-key/regenerate';

      const res = await axios.post(endpoint, { password: apiKeyPasswordInput });

      if (res.data.success) {
        if (apiKeyAction === 'reveal') {
          setRevealedApiKey(res.data.apiKey);
          message.success('API Key revealed successfully');
        } else {
          setRevealedApiKey(res.data.apiKey);
          fetchApiKey();
          message.success('API Key regenerated successfully');
        }
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Verification failed';
      message.error(msg);
    } finally {
      setApiKeyVerifyLoading(false);
      setApiKeyPasswordInput('');
    }
  };

  const handleCopyApiKey = async () => {
    if (!revealedApiKey) return;
    // navigator.clipboard can be undefined on insecure origins (HTTP) or
    // throw if the document isn't focused — fall back to a hidden textarea
    // so users always get *something* on copy.
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(revealedApiKey);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = revealedApiKey;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      message.success('API Key copied to clipboard');
    } catch (err) {
      console.error('Failed to copy API key:', err);
      message.error('Could not copy automatically — please copy manually');
    }
  };

  const handleCloseApiKeyModal = () => {
    setShowApiKeyModal(false);
    setApiKeyPasswordInput('');
    setRevealedApiKey(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  const statusColorMap = {
    approved: 'green',
    pending: 'orange',
    rejected: 'red',
    suspended: 'volcano',
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>My Account</Title>
        <Text type="secondary">Manage your personal and company information</Text>
      </div>

      <Alert
        message="Required for Voucher Purchase"
        description="Fields marked with * are required by voucher providers (GoWabi, Zhongmin) to process purchases. Please ensure they are filled in before placing orders."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        requiredMark="optional"
      >
        {/* Account Status */}
        <Card
          title="Account Status"
          style={{ marginBottom: 24 }}
          size="small"
        >
          <Row gutter={24}>
            <Col span={8}>
              <Text type="secondary">Status</Text>
              <div style={{ marginTop: 4 }}>
                <Tag color={statusColorMap[profileData?.status] || 'default'}>
                  {(profileData?.status || 'unknown').toUpperCase()}
                </Tag>
              </div>
            </Col>
            <Col span={8}>
              <Text type="secondary">Tier</Text>
              <div style={{ marginTop: 4 }}>
                <Tag color="purple">{profileData?.tier || 'basic'}</Tag>
              </div>
            </Col>
            <Col span={8}>
              <Text type="secondary">Currency</Text>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue">{profileData?.currency || 'USD'}</Tag>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Personal Information */}
        <Card
          title="Personal Information"
          style={{ marginBottom: 24 }}
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              * Required for GoWabi purchases
            </Text>
          }
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'First name is required' }]}
                tooltip="Required by GoWabi (customer_name)"
              >
                <Input prefix={<UserOutlined />} placeholder="Enter first name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Last name is required' }]}
                tooltip="Required by GoWabi (customer_name)"
              >
                <Input prefix={<UserOutlined />} placeholder="Enter last name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label={
                  <span>
                    Email
                    <Tag color="red" style={{ marginLeft: 8, fontSize: 11 }}>Required for Purchase</Tag>
                  </span>
                }
                rules={[
                  { required: true, message: 'Email is required for voucher purchases (GoWabi)' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
                tooltip="Required by GoWabi (customer_email)"
              >
                <Input prefix={<MailOutlined />} placeholder="Enter email" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label={
                  <span>
                    Phone Number
                    <Tag color="red" style={{ marginLeft: 8, fontSize: 11 }}>Required for Purchase</Tag>
                  </span>
                }
                rules={[{ required: true, message: 'Phone number is required for voucher purchases (GoWabi & Zhongmin)' }]}
                tooltip="Required by GoWabi (phone_number) and Zhongmin (account)"
              >
                <Input prefix={<PhoneOutlined />} placeholder="Enter phone number" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Company Information */}
        <Card
          title="Company Information"
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="companyName"
                label="Company Name"
                rules={[{ required: true, message: 'Company name is required' }]}
              >
                <Input prefix={<ShopOutlined />} placeholder="Enter company name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Contact Person Name"
              >
                <Input prefix={<UserOutlined />} placeholder="Enter contact person name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="Address"
          >
            <Input prefix={<HomeOutlined />} placeholder="Enter address" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="country"
                label="Country"
              >
                <Select
                  showSearch
                  placeholder="Select country"
                  optionFilterProp="children"
                  suffixIcon={<GlobalOutlined />}
                >
                  {countryOptions.map(c => (
                    <Option key={c.code} value={c.code}>{c.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="currency"
                label="Settlement Currency"
                rules={[{ required: true, message: 'Currency is required' }]}
              >
                <Select
                  showSearch
                  placeholder="Select currency"
                  optionFilterProp="children"
                >
                  <Option value="USD">USD - US Dollar</Option>
                  <Option value="EUR">EUR - Euro</Option>
                  <Option value="GBP">GBP - British Pound</Option>
                  <Option value="JPY">JPY - Japanese Yen</Option>
                  <Option value="CNY">CNY - Chinese Yuan</Option>
                  <Option value="HKD">HKD - Hong Kong Dollar</Option>
                  <Option value="SGD">SGD - Singapore Dollar</Option>
                  <Option value="AUD">AUD - Australian Dollar</Option>
                  <Option value="CAD">CAD - Canadian Dollar</Option>
                  <Option value="KRW">KRW - Korean Won</Option>
                  <Option value="THB">THB - Thai Baht</Option>
                  <Option value="MYR">MYR - Malaysian Ringgit</Option>
                  <Option value="PHP">PHP - Philippine Peso</Option>
                  <Option value="VND">VND - Vietnamese Dong</Option>
                  <Option value="IDR">IDR - Indonesian Rupiah</Option>
                  <Option value="INR">INR - Indian Rupee</Option>
                  <Option value="AED">AED - UAE Dirham</Option>
                  <Option value="BRL">BRL - Brazilian Real</Option>
                  <Option value="NZD">NZD - New Zealand Dollar</Option>
                  <Option value="CHF">CHF - Swiss Franc</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Divider />

        <div style={{ textAlign: 'right' }}>
          <Button onClick={() => form.resetFields()} style={{ marginRight: 12 }}>
            Reset
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            icon={<SaveOutlined />}
            size="large"
          >
            Save Changes
          </Button>
        </div>
      </Form>

      <Divider />

      {/* API Key */}
      <Card
        title={
          <Space>
            <KeyOutlined />
            <span>API Key</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
        loading={apiKeyLoading}
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            Use this API key to authenticate requests to the VouChain API.
            Keep it secure and never share it publicly.
          </Text>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            background: '#f5f5f5',
            borderRadius: 8,
            fontFamily: 'monospace',
            fontSize: 15,
          }}
        >
          <LockOutlined style={{ color: '#8c8c8c' }} />
          <Text
            style={{
              flex: 1,
              fontFamily: 'monospace',
              letterSpacing: 1,
              fontSize: 15,
            }}
          >
            {maskedApiKey || 'No API key generated'}
          </Text>

          <Space>
            <Tooltip title="Reveal API Key">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => openApiKeyModal('reveal')}
                disabled={!maskedApiKey}
              />
            </Tooltip>
            <Tooltip title="Regenerate API Key">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => openApiKeyModal('regenerate')}
                disabled={!maskedApiKey}
              />
            </Tooltip>
          </Space>
        </div>

        {apiKeyCreatedAt && (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Created: {dayjs(apiKeyCreatedAt).format('MMM DD, YYYY HH:mm')}
            </Text>
          </div>
        )}
      </Card>

      {/* API Key Password Verification Modal */}
      <Modal
        title={
          apiKeyAction === 'reveal'
            ? 'Enter Password to Reveal API Key'
            : 'Enter Password to Regenerate API Key'
        }
        open={showApiKeyModal}
        onCancel={handleCloseApiKeyModal}
        footer={null}
        destroyOnClose
      >
        {!revealedApiKey ? (
          <div>
            <Text
              type="secondary"
              style={{ display: 'block', marginBottom: 16 }}
            >
              {apiKeyAction === 'reveal'
                ? 'For security, please verify your identity by entering your account password.'
                : 'This will invalidate your current API key and generate a new one. Please enter your password to confirm.'}
            </Text>
            <Input.Password
              placeholder="Enter your password"
              value={apiKeyPasswordInput}
              onChange={(e) => setApiKeyPasswordInput(e.target.value)}
              onPressEnter={handleApiKeyPasswordSubmit}
              prefix={<LockOutlined />}
              size="large"
              style={{ marginBottom: 16 }}
            />
            <Button
              type="primary"
              block
              size="large"
              loading={apiKeyVerifyLoading}
              onClick={handleApiKeyPasswordSubmit}
            >
              {apiKeyAction === 'reveal' ? 'Verify & Reveal' : 'Verify & Regenerate'}
            </Button>
          </div>
        ) : (
          <div>
            <Alert
              type={apiKeyAction === 'reveal' ? 'info' : 'success'}
              message={
                apiKeyAction === 'reveal'
                  ? 'Your API Key'
                  : 'New API Key Generated'
              }
              description={
                apiKeyAction === 'regenerate'
                  ? 'Your old API key has been invalidated. Please update your integrations.'
                  : undefined
              }
              style={{ marginBottom: 16 }}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 16px',
                background: '#f5f5f5',
                borderRadius: 8,
                fontFamily: 'monospace',
                fontSize: 14,
                wordBreak: 'break-all',
              }}
            >
              <Text
                copyable={{ text: revealedApiKey }}
                style={{ flex: 1, fontFamily: 'monospace' }}
              >
                {revealedApiKey}
              </Text>
            </div>
            <Button
              icon={<CopyOutlined />}
              block
              style={{ marginTop: 12 }}
              onClick={handleCopyApiKey}
            >
              Copy to Clipboard
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MyAccount;
